import { GridColDef } from "@mui/x-data-grid";
import { useObrasDispatch, useObrasState } from "./obras-context";
import { ACTION_TYPES, initialDataRequest, IPendingFotoObra } from "./obras-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IObraResponse } from "../../../../model/admin-portal/response/obra-response";
import { IBasePagedRequest } from "../../../../model/base/request/base-paged-request";
import { IconButton, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';

export const useObrasController = () => {
    const state = useObrasState();
    const dispatch = useObrasDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: IBasePagedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listObras(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar obras' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const [situacoes, funcoes, tipos, contratos, convenios] = await Promise.all([
                AdminPortalAppService.listSituacoesObra(),
                AdminPortalAppService.listFuncoesObra(),
                AdminPortalAppService.listTiposObra(),
                AdminPortalAppService.listContratosLookup(),
                AdminPortalAppService.listConveniosLookup()
            ]);
            dispatch({ type: ACTION_TYPES.setSituacoesList, payload: situacoes });
            dispatch({ type: ACTION_TYPES.setFuncoesList, payload: funcoes });
            dispatch({ type: ACTION_TYPES.setTiposList, payload: tipos });
            dispatch({ type: ACTION_TYPES.setContratosList, payload: contratos });
            dispatch({ type: ACTION_TYPES.setConveniosList, payload: convenios });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar listas de apoio' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getObra(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idContrato: response.idContrato,
                    idConvenio: response.idConvenio ?? null,
                    idSituacaoObra: response.idSituacaoObra,
                    idFuncaoObra: response.idFuncaoObra,
                    idTipoObra: response.idTipoObra,
                    dtaInicio: response.dtaInicio,
                    dtaPrevisaoConclusao: response.dtaPrevisaoConclusao,
                    dtaConclusao: response.dtaConclusao ?? null,
                    valorObra: response.valorObra,
                    percentualExecutado: response.percentualExecutado ?? null
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar obra' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idContrato || !r.idSituacaoObra || !r.idFuncaoObra || !r.idTipoObra) return false;
        if (!r.dtaInicio || !r.dtaPrevisaoConclusao) return false;
        if (!r.valorObra || r.valorObra <= 0) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IObraResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setPendingFotos, payload: [] });
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        dispatch({ type: ACTION_TYPES.setPendingFotos, payload: [] });
    };

    const addPendingFoto = (foto: IPendingFotoObra) => {
        dispatch({ type: ACTION_TYPES.setPendingFotos, payload: [...state.pendingFotos, foto] });
    };

    const removePendingFoto = (tempId: string) => {
        dispatch({ type: ACTION_TYPES.setPendingFotos, payload: state.pendingFotos.filter((f) => f.tempId !== tempId) });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        const isEditing = state.dataRequest.id !== undefined && state.dataRequest.id !== null;

        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.addUpdateObra(state.dataRequest);
            const idObra: number = isEditing ? (state.dataRequest.id as number) : response?.data?.id;

            if (state.pendingFotos.length > 0 && idObra !== undefined && idObra !== null) {
                for (const foto of state.pendingFotos) {
                    await AdminPortalAppService.addUpdateFotoObra(
                        { id: undefined, idObra, descricao: foto.descricao, dtaPublicacao: foto.dtaPublicacao },
                        foto.file
                    );
                }
            }

            showMessage({
                type: 'success',
                message: isEditing ? 'Obra atualizada com sucesso' : 'Obra criada com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar obra' });
        } finally {
            showBackDrop(false);
        }
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'contratoNumero', headerName: 'Nº Contrato', flex: 1, minWidth: 130 },
            { field: 'tipoObraName', headerName: 'Tipo de Obra', flex: 1, minWidth: 150 },
            { field: 'funcaoObraName', headerName: 'Função', flex: 1, minWidth: 130 },
            {
                field: 'situacaoObraName', headerName: 'Situação', width: 140,
                renderCell: (params) => <Chip label={params.value} size="small" />
            },
            {
                field: 'valorObra', headerName: 'Valor', width: 150, align: 'right', headerAlign: 'right',
                renderCell: (params) => params.value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        addPendingFoto,
        removePendingFoto,
        save,
        getColumnsGrid
    };
};
